import { useQuery } from '@apollo/client'
import { Close as CloseIcon } from '@mui/icons-material'
import { Dialog, DialogContent, DialogTitle, IconButton, styled } from '@mui/material'
import { Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly'
import { useFlag } from '@unleash/proxy-client-react'
import { capitalize } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import config from '../../../../config'
import { SubscriptionTokenType, UserProfileDocument } from '../../../../graphql/generated'
import useRum from '../../../../providers/rum/useRum'
import logger from '../../../../utils/logger'
import PaymentForm from '../../../subscription/components/payment-recurly/PaymentForm'
import SubscriptionErrorCodes from '../../../subscription/errorCodes'
import useSubscriptionCreate from '../../../subscription/hooks/useSubscriptionCreate'
import SuccessModal from './SuccessModal'

interface RecurlyToken {
  type: string
  id: string
}

interface Errors {
  general?: string | null
  card?: string | null
  first_name?: string | null
  last_name?: string | null
  country?: string | null
  postal_code?: string | null
  paypal?: string | null
  apple?: string | null
  cashapp?: string | null
}

interface PaymentModalProps {
  open: boolean
  onClose: () => void
  planName: string
  billingCycle: string
  total: string
  buttonText: string
  content: string | undefined
  title: string
}

const transformErrorMessage = (message: string) => {
  if (message.includes('already has subscription')) {
    return 'You already have a subscription. Please contact support if you need assistance.' // Return null to indicate this error should be ignored
  }
  if (message.includes('already in progress')) {
    logger.debug('Purchase is already in progress, ignoring error.')
    return null // Return null to indicate this error should be ignored
  }
  return message
}

// Styled components for the modal
const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    overflow: 'hidden',
    maxWidth: '550px',
    width: '100%',
    margin: '16px',
    '@media (min-width: 768px)': {
      maxWidth: '600px'
    }
  }
}))

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  color: 'black',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent'
  }
})

function PaymentModal({ open, onClose, planName, billingCycle, total, buttonText, content, title }: PaymentModalProps) {
  const { generateErrorEvent, recordError, recordApolloError } = useRum()
  const recordApolloErrorRef = useRef(recordApolloError)
  const enableCashApp = useFlag('enableCashApp')
  const formRef = useRef<HTMLFormElement>(null)
  const [token, setToken] = useState<RecurlyToken | null>(null)
  const [tokenType, setTokenType] = useState<SubscriptionTokenType | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const recurly = useRecurly()
  const userProfile = useQuery(UserProfileDocument, { fetchPolicy: 'cache-first' })
  const currentBucketId = userProfile.data?.userProfile?.currentBucketId

  const {
    createSubscription,
    error: createSubscriptionError,
    called: createSubscriptionCalled,
    called: createSubscriptionLoading,
    success: createSubscriptionSuccess
  } = useSubscriptionCreate({ redirectOnSuccess: false, price: total })

  const createSubscriptionRef = useRef(createSubscription)
  const [errors, setErrors] = useState<Errors>({})

  const hasPendingOrSuccessfulRequest =
    (!createSubscriptionError && createSubscriptionCalled && createSubscriptionLoading) || createSubscriptionSuccess

  // Show success modal when subscription is successful
  useEffect(() => {
    if (createSubscriptionSuccess) {
      setShowSuccessModal(true)
    }
  }, [createSubscriptionSuccess])

  const handleSuccessClose = () => {
    setShowSuccessModal(false)
    onClose()

    if (createSubscriptionSuccess) {
      if (currentBucketId) {
        window.location.href = `/bucket/${currentBucketId}`
      } else {
        window.location.href = '/buckets'
      }
    }
  }

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (!formRef.current) return
      event.preventDefault()
      recurly.token(formRef.current, async (err, tokenResponse) => {
        if (err?.details?.length) {
          logger.error('[error]', err)
          const updatedErrors = {} as Errors
          err.details.forEach((error) => {
            let field = error.field as keyof Errors
            let message = error.messages.join(', ')
            if (['number', 'month', 'year', 'cvv'].includes(field)) {
              field = 'card'
              message = 'Credit card error, expiration month, and expiration year are required. Most cards also require a CVV.'
            } else {
              // We don't need to record field validation errors
              const errorEvent = generateErrorEvent(SubscriptionErrorCodes.RecurlyTokenGenerationFailed, { message, context: { error } })
              recordError(errorEvent)
              message = capitalize(message)
            }
            const transformedMessage = transformErrorMessage(message)
            if (transformedMessage !== null) {
              updatedErrors[field] = transformedMessage
            }
          })
          if (Object.keys(updatedErrors).length > 0) {
            setErrors(updatedErrors)
          }
        } else if (tokenResponse) {
          setTokenType(SubscriptionTokenType.CreditCard)
          setToken(tokenResponse)
        }
      })
    },
    [recurly, generateErrorEvent, recordError]
  )

  // Handle subscription creation when token is available
  useEffect(() => {
    if (!token?.id || !tokenType) {
      return
    }

    const plan = `${planName}-${billingCycle}`
    createSubscriptionRef.current({ token: token.id, planCode: plan, tokenType, threeDSToken: null }, (apolloError) => {
      recordApolloErrorRef.current(SubscriptionErrorCodes.RecurlyFinalizeFailed, apolloError)
      setTokenType(null)
      const errorMessage = transformErrorMessage(apolloError.message)
      if (errorMessage === null) {
        setErrors({})
      } else {
        setErrors({ general: errorMessage })
      }
    })
  }, [token?.id, tokenType, planName, billingCycle])

  return (
    <>
      <StyledDialog open={open} onClose={onClose}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: { xs: '1.3rem', sm: '1.5rem' }, pb: 1, position: 'relative' }}>
          {title}
          <CloseButton onClick={onClose} size="small">
            <CloseIcon />
          </CloseButton>
        </DialogTitle>
        {content && <DialogContent sx={{ textAlign: 'center', pb: 1, px: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>{content}</DialogContent>}
        <DialogContent sx={{ pt: 1, px: 3, pb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          <PaymentForm
            enableCashApp={enableCashApp}
            hasPendingOrSuccessfulRequest={hasPendingOrSuccessfulRequest}
            buttonText={buttonText}
            handleSubmit={handleSubmit}
            formRef={formRef}
            errors={errors}
            setErrors={setErrors}
            setToken={setToken}
            setTokenType={setTokenType}
            planName={planName}
            billingCycle={billingCycle}
            total={total}
          />
        </DialogContent>
      </StyledDialog>
      <SuccessModal open={showSuccessModal} onClose={handleSuccessClose} />
    </>
  )
}

export default function Payment({
  open = false,
  onClose = () => {},
  planName = 'sharing',
  billingCycle = 'monthly',
  total = '8.00',
  buttonText = 'Complete Upgrade',
  content,
  title = 'Complete Your Upgrade!'
}: Partial<PaymentModalProps>) {
  return (
    <RecurlyProvider publicKey={config.recurlyApiKey}>
      <Elements>
        <PaymentModal
          open={open}
          onClose={onClose}
          planName={planName}
          billingCycle={billingCycle}
          total={total}
          buttonText={buttonText}
          content={content}
          title={title}
        />
      </Elements>
    </RecurlyProvider>
  )
}
